import MidiEffect from './MidiEffect.js';
import VariableEffect from './scripting/VariableEffect.js';
import Resolution from 'engine/Resolution.js';
import {getStep} from './tidal/tidal.js';
import {parse, getCycleString} from './tidal/parser.js';
import {AbstractParameter} from 'parameters/AbstractParameter.js';
import MidiTrigger from './MidiTrigger.js';
import WarpEffect from './WarpEffect.js';
import Tone from 'tone';

const TIME_16N = new Tone.Time('16n');
const TIME_32N = new Tone.Time('32n');

class TidalEffect extends VariableEffect(WarpEffect) {
    constructor(track) {
        super(track, false);
        this.name = 'Tidal';
        this.cycleParameter = new AbstractParameter(
            'cycle', this, (o, x) => {
                if (x) {
                    let values = o.clearVariables();
                    o.handleVariables(x, values);
                }
            },
            {
                defaultValue: undefined,
                min: false,
                max: true,
                unit: '',
                automatable: false
            });

        this.registerParameter(this.cycleParameter);
        this.warpLengthParameter.automatable = false;
        this.warpFactorParameter.automatable = false;
        this.loopLengthParameter.automatable = false;
        this.resolutionParameter.automatable = false;
        this.resolutionParameter.hide = true;
        this.warpLengthParameter.hide = true;
        this.warpFactorParameter.hide = true;
        this.loopLengthParameter.hide = true;
        this.durationFactorParameter.hide = true;
        this.smoothnessParameter.hide = true;

        this.cycleLengthParameter = new AbstractParameter(
            'cycleLength', this, (o, x) => [],
            {
                documentation: "The number of 1/32th steps per cycle",
                prettyName: "Cycle Length",
                defaultValue: 32,
                min: 4,
                max: 64,
                unit: ''
            });

        this.registerParameter(this.cycleLengthParameter);

        this.lengthFactorParameter = new AbstractParameter(
            'lengthFactor', this, (o, x) => [],
            {
                documentation: "Multiplies the speed of the cycle",
                defaultValue: 1,
                prettyName: "Speed Factor",
                min: 0.25,
                max: 8,
                unit: ''
            });

        this.registerParameter(this.lengthFactorParameter);

        this.cycleRetrigger = true;

        this.resolution = new Resolution(32, false);

        this.cycle = parse('[0 12 ~ 12]');

        // TODO: look through the expression to see how many outputs this needs
        this.numberOfOutputs = 2;
    }


    get documentation() {
        return [
            "A tidal-cycles inspired scripting effect. Write expressions for cyclic beats using tidal notation.",
            "Type or press enter to execute",
        ];
    }

    isRealTime() {
        return false;
    }

    get cycleLength() {
        return this.cycleLengthParameter.get();
    }

    set cycleLength(cycleLength) {
        return this.cycleLengthParameter.set(cycleLength);
    }

    get lengthFactor() {
        return this.lengthFactorParameter.get();;
    }

    set lengthFactor(len) {
        return this.lengthFactorParameter.set(len);
    }

    get cycle() {
        return this.cycleParameter.get();
    }

    set cycle(cycle) {
        return this.cycleParameter.set(cycle);
    }

    removeNote(note, sliceNumber, skip=!this.gated) {
        super.removeNote(note, sliceNumber, skip);
    }

    handleVariables(cycle, values=[]) {
        // go thru the cycle and look for variables
        for (let part of cycle) {
            if (Array.isArray(part)) {
                this.handleVariables(part);
            } else {
                if (part.cycle) {
                    this.handleVariables(part.cycle);
                }
                if (part.transpose && typeof part.transpose === "object") {
                    // its a variable
                    this.newVar(part.transpose.name, 0, -32, 32);
                } 
                if (part.operand && typeof part.operand === "object") {
                    this.newVar(part.operand.name, 2, 1, 16);
                }
            }
        }

        for (let name in values) {
            let param = this[name + 'Parameter'];
            if (param) {
                param.copyFrom(
                    values[name], this.track.sequencerEngine);
                if (param.isMapped) {
                    param.isMapped.replaceParameter(param);
                }
            }
        }
        this.updateVariablesRedux();
    }

    get cycleString() {
        return getCycleString(this.cycle);
    }

    getText() {
        return this.cycleString;
    }

    replaceOperandVariables(cycle) {
        let _cycle = [];
        if (!cycle) {
            return _cycle;
        }
        // go thru the cycle and look for variables
        for (let part of cycle) {
            if (Array.isArray(part)) {
                _cycle.push(this.replaceOperandVariables(part));
            } else {
                let _part = {... part};
                if (_part.cycle) {
                    _part.cycle = this.replaceOperandVariables(part.cycle);
                }
                if (part.operand && typeof part.operand === "object") {
                    this.newVar(part.operand.name, 2, 1, 16);
                    _part.operand = this.var(part.operand.name)();
                }
                _cycle.push(_part);
            }
        }
        return _cycle;
    }

    getTrigger(
        stepData,
        note,
        time,
        stepNumber,
        actualStepNumber,
        sliceNumber) {
        if (this.stepNumberTriggered === undefined) {
            if (this.gated || this.sliceRetrigger || this.isNothingPlaying()) {
                this.stepNumberTriggered = stepNumber;
            }
        }
 
        if (this.cycle === undefined) {
            return undefined;
        }
        let stepsElapsed =  stepNumber - this.stepNumberTriggered;
        if (stepsElapsed < 0) {
            stepsElapsed  = stepsElapsed + this.track.length;
        }

        if (!this.cycleRetrigger) {
            stepsElapsed = stepNumber;
        }

        let cycleSteps = getStep({
            cycle: this.replaceOperandVariables(this.cycle),
            stepNumber: stepsElapsed,
            length: Math.floor(this.cycleLength / this.lengthFactor)
        });
        if (cycleSteps.length > 0) {
            this.currentCycles = cycleSteps;
        }
        let triggers = [];
        for (let i in cycleSteps) {
            let {outputNumber, transpose, offset, duration} = cycleSteps[i];
            if (transpose === null) {
                continue;
            }
            if (typeof transpose === "object") {
                // its a variable
                transpose = this.var(transpose.name)();
            }
            stepData = this.getStep(stepData, note, 1, true);
            stepData.slice = sliceNumber;
            stepData.transpose = parseFloat(stepData.transpose) + parseFloat(transpose);
            stepData.duration = duration * this.durationFactor;
            stepData.on = true;
            triggers.push(new MidiTrigger(
                stepData, TIME_32N * offset + time, actualStepNumber, outputNumber));
        }
        return triggers;
    }

    getCopy(track, ignoreId=true) {
        let c = new TidalEffect(track);
        c.fromJson(this.getJson(), ignoreId);
        return c;
    }

    fromJson(json, isCopy) {
        this.cycleParameter.set(json.cycle);
        let bulk = super.fromJson(json, isCopy);
        return bulk;
    }
}

export default TidalEffect;
